import apiClient from './client';
import type {
  AcceptInvitationDto,
  CreateLinkInvitationDto,
  CreateEmailInvitationDto,
  CreatePhoneInvitationDto,
  ExternalProjectEmailInvitationDto,
  ExternalProjectInvitation,
  ExternalProjectInvitationListResponse,
  ExternalProjectPhoneInvitationDto,
  ExternalProjectInvitationReviewDto,
  InvitationPreview,
  OrgInvitation,
} from '@/types/org';
import { keysToCamelCase } from '@/lib/api-utils';

export const orgInvitationsApi = {
  /** 创建链接邀请 */
  async createLinkInvitation(orgId: string, dto?: CreateLinkInvitationDto): Promise<OrgInvitation> {
    return apiClient.post<OrgInvitation>(`/orgs/${orgId}/invitations/link`, {
      targetGroupId: dto?.targetGroupId,
      reportAccessScope: dto?.reportAccessScope ?? 0,
      // 不传时由后端按 admin 系统配置解析有效期；仅在显式指定时下发（单位：分钟）
      ...(dto?.expiresInMinutes != null ? { expiresInMinutes: dto.expiresInMinutes } : {}),
    }) as unknown as Promise<OrgInvitation>;
  },

  /** 创建手机号邀请 */
  async createPhoneInvitation(
    orgId: string,
    dto: CreatePhoneInvitationDto
  ): Promise<OrgInvitation> {
    return apiClient.post<OrgInvitation>(`/orgs/${orgId}/invitations/phone`, {
      inviteePhone: dto.inviteePhone,
      targetGroupId: dto.targetGroupId,
      reportAccessScope: dto.reportAccessScope ?? 0,
    }) as unknown as Promise<OrgInvitation>;
  },

  /** 创建邮箱邀请 */
  async createEmailInvitation(
    orgId: string,
    dto: CreateEmailInvitationDto
  ): Promise<OrgInvitation> {
    return apiClient.post<OrgInvitation>(`/orgs/${orgId}/invitations/email`, {
      inviteeEmail: dto.inviteeEmail,
      targetGroupId: dto.targetGroupId,
      reportAccessScope: dto.reportAccessScope ?? 0,
    }) as unknown as Promise<OrgInvitation>;
  },

  /** 预览邀请信息（加入前确认） */
  async previewInvitation(token: string): Promise<InvitationPreview> {
    return apiClient.get<InvitationPreview>(
      `/orgs/invitations/${token}/preview`
    ) as unknown as Promise<InvitationPreview>;
  },

  /** 接受邀请并加入组织 */
  async acceptInvitation(dto: AcceptInvitationDto): Promise<void> {
    await apiClient.post('/orgs/invitations/accept', {
      token: dto.token,
      invitationId: dto.invitationId,
    });
  },

  /** 拒绝邀请 */
  async declineInvitation(dto: AcceptInvitationDto): Promise<void> {
    await apiClient.post('/orgs/invitations/decline', {
      token: dto.token,
      invitationId: dto.invitationId,
    });
  },

  /** 撤销邀请 */
  async cancelInvitation(orgId: string, invitationId: string): Promise<void> {
    await apiClient.delete(`/orgs/${orgId}/invitations/${invitationId}`);
  },

  async createExternalProjectInvitation(
    projectId: string,
    data: { expiresInHours?: number }
  ): Promise<ExternalProjectInvitation> {
    const response = await apiClient.post(
      `/projects/${projectId}/external-member-invitations/link`,
      data
    );
    return keysToCamelCase<ExternalProjectInvitation>(response);
  },

  async createExternalProjectPhoneInvitation(
    projectId: string,
    data: ExternalProjectPhoneInvitationDto
  ): Promise<ExternalProjectInvitation> {
    const response = await apiClient.post(
      `/projects/${projectId}/external-member-invitations/phone`,
      data
    );
    return keysToCamelCase<ExternalProjectInvitation>(response);
  },

  async createExternalProjectEmailInvitation(
    projectId: string,
    data: ExternalProjectEmailInvitationDto
  ): Promise<ExternalProjectInvitation> {
    const response = await apiClient.post(
      `/projects/${projectId}/external-member-invitations/email`,
      data
    );
    return keysToCamelCase<ExternalProjectInvitation>(response);
  },

  async listExternalProjectInvitations(
    orgId: string
  ): Promise<ExternalProjectInvitationListResponse> {
    const response = await apiClient.get(`/orgs/${orgId}/external-member-invitations`);
    return keysToCamelCase<ExternalProjectInvitationListResponse>(response);
  },

  async reviewExternalProjectInvitation(
    orgId: string,
    invitationId: string,
    data: ExternalProjectInvitationReviewDto
  ): Promise<ExternalProjectInvitation> {
    const response = await apiClient.post(
      `/orgs/${orgId}/invitations/${invitationId}/external-project-review`,
      data
    );
    return keysToCamelCase<ExternalProjectInvitation>(response);
  },
};
